import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Chip, Tooltip, Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Gavel as OperationalIcon,
  Cloud as AzureGovIcon,
} from '@mui/icons-material';
import { get, authPost, authPut, authDelete } from '../../services/apiService';
import { isAdmin } from '../../services/authService';

const PRIMARY      = '#0f1f3d';
const PRIMARY_DARK = '#080f1e';
const MUTED        = '#666666';
const SURFACE      = '#F8F9FA';

const PILLARS = [
  { key: 'operationalCompliance', label: 'Operational Compliance', icon: OperationalIcon, color: '#1565c0' },
  { key: 'azureGovCompliance',    label: 'Azure Gov Compliance',   icon: AzureGovIcon,    color: '#6a1b9a' },
];

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY }
};

const H22026SubCategoryAdmin = () => {
  const [loading, setLoading]         = useState(true);
  const [subCategories, setSubCategories] = useState([]);
  const [error, setError]             = useState(null);
  const [success, setSuccess]         = useState(null);

  const [dialogOpen, setDialogOpen]   = useState(false);
  const [dialogPillar, setDialogPillar] = useState(null);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState({ name: '', alias: '' });
  const [saving, setSaving]           = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await get('/api/compliance-h2-2026/subcategories');
      if (res.success) setSubCategories(res.data || []);
      else setError('Failed to load sub-categories');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const openCreate = (pillar) => {
    setEditing(null);
    setDialogPillar(pillar);
    setForm({ name: '', alias: '' });
    setDialogOpen(true);
  };

  const openEdit = (sub) => {
    const pillar = PILLARS.find(p => p.key === sub.pillarKey);
    setEditing(sub);
    setDialogPillar(pillar);
    setForm({ name: sub.name, alias: sub.alias });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.alias.trim()) { setError('Name and alias are required'); return; }
    try {
      setSaving(true); setError(null);
      const res = editing
        ? await authPut(`/api/compliance-h2-2026/subcategories/${editing._id}`, { name: form.name, alias: form.alias })
        : await authPost('/api/compliance-h2-2026/subcategories', {
            pillarKey: dialogPillar.key, name: form.name, alias: form.alias
          });
      if (res.success) {
        setSuccess(editing ? 'Sub-category updated.' : 'Sub-category created.');
        setTimeout(() => setSuccess(null), 4000);
        setDialogOpen(false);
        loadData();
      } else { setError(res.message || 'Failed to save'); }
    } catch (e) { setError(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true); setError(null);
      const res = await authDelete(`/api/compliance-h2-2026/subcategories/${deleteTarget._id}`);
      if (res.success) {
        setSuccess('Sub-category deleted.');
        setTimeout(() => setSuccess(null), 4000);
        setDeleteTarget(null);
        loadData();
      } else { setError(res.message || 'Failed to delete'); }
    } catch (e) { setError(e.response?.data?.message || 'Failed to delete'); }
    finally { setDeleting(false); }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>H2-2026 Initiative Categories</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define initiative categories for each pillar. Features tagged with{' '}
        <code>compliance-category-&lt;alias&gt;</code> in ADO will be grouped under the matching category.
      </Typography>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {PILLARS.map(pillar => {
        const Icon = pillar.icon;
        const cats = subCategories.filter(sc => sc.pillarKey === pillar.key);
        return (
          <Paper key={pillar.key} variant="outlined" sx={{ mb: 3, borderRadius: '12px', overflow: 'hidden' }}>
            <Box sx={{
              px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
              bgcolor: `${pillar.color}0d`, borderBottom: '1px solid rgba(0,0,0,0.08)',
            }}>
              <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: `${pillar.color}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon sx={{ fontSize: 18, color: pillar.color }} />
              </Box>
              <Typography fontWeight={650} sx={{ flex: 1, fontSize: '0.9rem' }}>{pillar.label}</Typography>
              {isAdmin() && (
                <Button size="small" startIcon={<AddIcon />} onClick={() => openCreate(pillar)}
                  sx={{ bgcolor: pillar.color, color: '#fff', '&:hover': { bgcolor: pillar.color, opacity: 0.9 }, borderRadius: '8px', textTransform: 'none', fontSize: '0.78rem' }}>
                  Add
                </Button>
              )}
            </Box>

            {cats.length === 0 ? (
              <Box px={3} py={2.5} textAlign="center">
                <Typography variant="body2" color={MUTED}>No categories yet.</Typography>
              </Box>
            ) : (
              <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {cats.map(sc => (
                  <Box key={sc._id} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1, borderRadius: '8px', border: '1px solid rgba(0,0,0,0.06)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={500}>{sc.name}</Typography>
                      <Typography variant="caption" color={MUTED} sx={{ fontFamily: 'monospace' }}>
                        compliance-category-{sc.alias}
                      </Typography>
                    </Box>
                    {isAdmin() && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(sc)} sx={{ p: 0.5 }}>
                            <EditIcon sx={{ fontSize: 16, color: MUTED }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteTarget(sc)} sx={{ p: 0.5 }}>
                            <DeleteIcon sx={{ fontSize: 16, color: '#e53935' }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        );
      })}

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editing ? 'Edit Category' : `Add Category — ${dialogPillar?.label}`}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField label="Name" size="small" fullWidth sx={inputSx}
              value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            <TextField label="Alias (kebab-case)" size="small" fullWidth sx={inputSx}
              value={form.alias} onChange={e => setForm(p => ({ ...p, alias: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
              helperText="Used in ADO tag: compliance-category-{alias}" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} variant="contained" sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteTarget?.name}</strong>? Features tagged with{' '}
            <code>compliance-category-{deleteTarget?.alias}</code> will appear as untagged.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} disabled={deleting} color="error" variant="contained">
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default H22026SubCategoryAdmin;
